"use client";
import React, { useState, useMemo } from "react";
import {
  LanguageType,
  VOWEL_FORMS,
  PHONETIC_SEQUENCES,
  LANGUAGE_CONFIGS,
} from "../service/multilingual-input.service";
import {
  X,
  Keyboard,
  Search,
  Sparkles,
  BookOpen,
  Layers,
  Zap,
} from "lucide-react";

interface PhoneticGuideProps {
  language: LanguageType;
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIES = [
  { id: "vowels", name: "Vowels", icon: <Sparkles className="w-4 h-4" /> },
  {
    id: "consonants",
    name: "Consonants",
    icon: <Keyboard className="w-4 h-4" />,
  },
  { id: "rules", name: "Rules", icon: <Layers className="w-4 h-4" /> },
];

export function PhoneticGuide({
  language,
  isOpen,
  onClose,
}: PhoneticGuideProps) {
  const [activeTab, setActiveTab] = useState("vowels");
  const [searchQuery, setSearchQuery] = useState("");

  const script = language.includes("Hindi") ? "Hindi" : "Gujarati";
  const vowelsData = VOWEL_FORMS[script] || {};
  const config = LANGUAGE_CONFIGS[language];

  const consonants = useMemo(() => {
    if (!config || language === "English") return [];
    return Object.entries(config.mapping)
      .filter(([_, val]) => {
        const isVowelMatra = Object.values(vowelsData).some(
          (v) => v.full === val.default || v.matra === val.default,
        );
        const isPunctuation = /[.,/;:'"\[\]{}\\=`\-_]/.test(val.default);
        return !isVowelMatra && !isPunctuation;
      })
      .map(([key, val]) => ({
        key: key.replace("Key", "").toLowerCase(),
        char: val.default,
        shiftChar: val.shift,
      }));
  }, [language, vowelsData, config]);

  const symbols = useMemo(() => {
    if (!config || language === "English") return [];
    return Object.entries(config.mapping)
      .filter(([_, val]) => /[.,/;:'"\[\]{}\\=`\-_]/.test(val.default))
      .map(([key, val]) => ({
        key: key.replace("Key", "").toLowerCase(),
        char: val.default,
        shiftChar: val.shift,
      }));
  }, [language, config]);

  if (!isOpen || language === "English" || !config) return null;

  const sequencesData = PHONETIC_SEQUENCES[script] || {};
  const filteredVowels = Object.entries(vowelsData).filter(
    ([key, data]) =>
      key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      data.full.includes(searchQuery),
  );

  const filteredSequences = Object.entries(sequencesData).filter(
    ([key, char]) =>
      key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      char.includes(searchQuery),
  );

  const CATEGORIES = [
    { id: "vowels", name: "Vowels", icon: <Sparkles className="w-4 h-4" /> },
    {
      id: "consonants",
      name: "Consonants",
      icon: <Keyboard className="w-4 h-4" />,
    },
    { id: "symbols", name: "Symbols", icon: <Zap className="w-4 h-4" /> },
    { id: "rules", name: "Rules", icon: <Layers className="w-4 h-4" /> },
  ];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-950/60 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl h-[85vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 border-b border-zinc-100 mb-2">
          <div className="flex items-center justify-between p-4 pb-2">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-zinc-900 rounded-xl flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-zinc-900 tracking-tight">
                  {config.name} Reference
                </h2>
                <p className="text-xs font-medium text-zinc-400 uppercase tracking-widest">
                  Phonetic Mapping System
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-400 hover:text-zinc-900"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-4 items-center p-4 pt-2">
            <div className="flex bg-zinc-100 p-1 rounded-2xl w-full md:w-auto">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all flex-1 md:flex-none ${
                    activeTab === cat.id
                      ? "bg-white text-zinc-900 shadow-sm"
                      : "text-zinc-500 hover:text-zinc-700"
                  }`}
                >
                  {cat.icon}
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                placeholder="Find a character or key..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-zinc-900/5 focus:border-zinc-900 transition-all"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-zinc-50/30">
          {activeTab === "vowels" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {filteredVowels.map(([key, data]) => (
                <div
                  key={key}
                  className="bg-white p-4 rounded-2xl border border-zinc-200 hover:border-zinc-900 transition-colors group"
                >
                  <div className="flex justify-between items-start mb-3">
                    <kbd className="text-[10px] font-bold px-2 py-0.5 bg-zinc-100 rounded text-zinc-500 group-hover:bg-zinc-900 group-hover:text-white transition-colors">
                      {key}
                    </kbd>
                    <span className="text-zinc-300 text-sm font-medium">
                      {data.matra || "—"}
                    </span>
                  </div>
                  <div className="text-4xl font-bold text-zinc-900">
                    {data.full}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "consonants" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {consonants
                .filter(
                  (c) =>
                    c.key.includes(searchQuery.toLowerCase()) ||
                    c.char.includes(searchQuery),
                )
                .map((c) => (
                  <div
                    key={c.key}
                    className="bg-white p-4 rounded-2xl border border-zinc-200 hover:border-zinc-900 transition-all group"
                  >
                    <div className="flex justify-between items-center pb-2 border-b border-zinc-50 mb-3">
                      <kbd className="text-xs font-bold text-zinc-400">
                        {c.key}
                      </kbd>
                      <span className="text-2xl font-bold text-zinc-900">
                        {c.char}
                      </span>
                    </div>
                    <div className="flex justify-between items-center opacity-60">
                      <span className="text-xs font-bold text-zinc-800">
                        Shift + {c.key}
                      </span>
                      <span className="text-lg font-semibold text-zinc-800">
                        {c.shiftChar}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {activeTab === "symbols" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {symbols
                .filter(
                  (s) =>
                    s.key.includes(searchQuery.toLowerCase()) ||
                    s.char.includes(searchQuery),
                )
                .map((s) => (
                  <div
                    key={s.key}
                    className="bg-white p-4 rounded-2xl border border-zinc-200 hover:border-zinc-900 transition-all group"
                  >
                    <div className="flex justify-between items-center pb-2 border-b border-zinc-50 mb-3">
                      <kbd className="text-xs font-bold text-zinc-400">
                        {s.key}
                      </kbd>
                      <span className="text-2xl font-bold text-zinc-900">
                        {s.char}
                      </span>
                    </div>
                    <div className="flex justify-between items-center opacity-60">
                      <span className="text-xs font-bold text-zinc-800">
                        Shift + {s.key}
                      </span>
                      <span className="text-lg font-semibold text-zinc-800">
                        {s.shiftChar}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {activeTab === "rules" && (
            <div className="mx-auto space-y-6">
              <div className="grid gap-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-zinc-900 rounded-3xl p-8 text-white">
                    <div className="flex items-center gap-3 mb-4 text-zinc-400">
                      <Zap className="w-5 h-5 fill-current" />
                      <span className="text-xs font-bold uppercase tracking-widest">
                        Conjunct Rule
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold mb-4">
                      The Halant Mechanism
                    </h3>
                    <p className="text-zinc-400 mb-6 leading-relaxed">
                      To create a half-character or conjunct, press{" "}
                      <kbd className="text-white bg-zinc-800 px-2 py-1 rounded mx-1">
                        q
                      </kbd>{" "}
                      immediately after any consonant.
                    </p>
                    <div className="bg-zinc-800/50 p-6 rounded-2xl flex items-center gap-4 text-lg">
                      <span className="font-mono text-zinc-500">
                        shak + q + ti
                      </span>
                      <span className="text-zinc-600">→</span>
                      <span className="text-3xl font-bold">
                        {script === "Hindi" ? "शक्ति" : "શક્તિ"}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white border border-zinc-200 p-8 rounded-3xl">
                    <h4 className="font-bold text-zinc-900 mb-2">
                      Natural Typing
                    </h4>
                    <p className="text-sm text-zinc-500 leading-relaxed mb-4">
                      Consonants are written in full by default. Simply type the
                      phonetic English equivalent.
                    </p>
                    <div className="text-xl font-bold text-zinc-900 p-4 bg-zinc-50 rounded-xl border border-dashed border-zinc-200">
                      {script === "Hindi" ? "tuje" : "temna"}{" "}
                      <span className="text-zinc-300 mx-2">→</span>{" "}
                      {script === "Hindi" ? "तुझे" : "તેમના"}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-zinc-50 border border-zinc-200 p-8 rounded-3xl">
                    <div className="flex items-center gap-3 mb-4 text-zinc-400">
                      <Zap className="w-5 h-5" />
                      <span className="text-xs font-bold uppercase tracking-widest">
                        R-Form Variants
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-zinc-900 mb-4">
                      Reph & Prakar
                    </h3>
                    <p className="text-zinc-500 mb-6 text-sm leading-relaxed">
                      Special forms of <strong>R</strong> are handled via
                      shortcuts:
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-zinc-100">
                        <span className="text-sm font-mono text-zinc-400">
                          Shift + R <span className="mx-1">or</span> rr
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400 italic">
                            Reph
                          </span>
                          <span className="text-2xl font-bold text-zinc-900">
                            {script === "Hindi" ? "र्" : "ર્"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-zinc-100">
                        <span className="text-sm font-mono text-zinc-400">
                          k + r <span className="mx-1">or</span> p + r
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400 italic">
                            Prakar
                          </span>
                          <span className="text-2xl font-bold text-zinc-900">
                            {script === "Hindi" ? "क्र" : "ક્ર"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white border border-zinc-200 p-8 rounded-3xl">
                    <h4 className="font-bold text-zinc-900 mb-2">
                      Smart Shortcuts
                    </h4>
                    <div className="space-y-3 mt-4">
                      {filteredSequences.map(([key, char]) => (
                        <div
                          key={key}
                          className="flex justify-between items-center py-2 border-b border-zinc-50 last:border-0"
                        >
                          <span className="text-xs font-mono text-zinc-600">
                            {key}
                          </span>
                          <span className="text-xl font-bold text-zinc-900">
                            {char}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-3 bg-white border-t border-zinc-100 flex items-center justify-between">
          <p className="text-xs text-zinc-400 font-medium">
            Type phonetically as you would speak.
          </p>
          <button
            onClick={onClose}
            className="px-10 py-3 bg-zinc-900 text-white text-sm font-bold rounded-2xl hover:bg-zinc-800 transition-all active:scale-95 shadow-xl shadow-zinc-200"
          >
            Acknowledge
          </button>
        </div>
      </div>
    </div>
  );
}
